import React, { useState, useEffect } from "react";
import { Download, Monitor, CheckCircle, Smartphone } from "lucide-react";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
}

interface InstallAppModalProps {
  isOpen: boolean;
  onClose: () => void;
  isDark: boolean;
}

export const InstallAppModal: React.FC<InstallAppModalProps> = ({
  isOpen,
  onClose,
  isDark,
}) => {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [installSuccess, setInstallSuccess] = useState(false);

  useEffect(() => {
    // Check if already installed
    if (
      window.matchMedia("(display-mode: standalone)").matches ||
      (window.navigator as any).standalone === true
    ) {
      setIsInstalled(true);
    }

    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };

    window.addEventListener("beforeinstallprompt", handler);
    window.addEventListener("appinstalled", () => {
      setIsInstalled(true);
      setInstallSuccess(true);
      setDeferredPrompt(null);
    });

    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
    };
  }, []);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      await deferredPrompt.prompt();
      const choiceResult = await deferredPrompt.userChoice;
      if (choiceResult.outcome === "accepted") {
        setInstallSuccess(true);
      }
      setDeferredPrompt(null);
    } else {
      // Fallback instructions if native prompt is not available
      alert(
        "To install Dualis Tools:\n• On Chrome/Edge desktop: Click the install icon (⊕) in the browser address bar.\n• On iOS Safari: Tap the Share button (⎋) and select 'Add to Home Screen'.\n• On Android Chrome: Tap menu (⋮) and select 'Install app' or 'Add to Home screen'."
      );
    }
  };

  if (!isOpen) return null;

  return (
    <div
      id="install-app-modal-backdrop"
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-fade-in"
      onClick={onClose}
    >
      <div
        id="install-app-modal-card"
        className={`w-full max-w-md rounded-2xl border shadow-2xl p-6 sm:p-8 space-y-6 transition-all ${
          isDark
            ? "bg-[#0f1322] border-slate-800 text-white"
            : "bg-white border-slate-200 text-slate-900"
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header Icon & Title */}
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-tr from-indigo-600 to-purple-600 p-0.5 shadow-lg shadow-indigo-500/25 flex items-center justify-center shrink-0">
            <img
              src="/logo.png"
              alt="Dualis Tools Logo"
              className="w-full h-full object-cover rounded-[14px]"
            />
          </div>
          <div>
            <h3 className="text-xl font-bold tracking-tight">Install Dualis Tools</h3>
            <p className={`text-xs ${isDark ? "text-slate-400" : "text-slate-500"}`}>
              Fast, Private & Offline-Ready Utilities
            </p>
          </div>
        </div>

        {/* Features Checklist */}
        <div
          className={`rounded-xl p-4 space-y-3 ${
            isDark ? "bg-slate-900/70 border border-slate-800/80" : "bg-slate-50 border border-slate-200/80"
          }`}
        >
          <div className="flex items-center gap-3 text-xs sm:text-sm font-medium">
            <CheckCircle className="w-4 h-4 text-emerald-500 shrink-0" />
            <span>Instant 1-click access from your desktop or home screen</span>
          </div>
          <div className="flex items-center gap-3 text-xs sm:text-sm font-medium">
            <CheckCircle className="w-4 h-4 text-emerald-500 shrink-0" />
            <span>Full-screen distraction-free workspace without URL bars</span>
          </div>
          <div className="flex items-center gap-3 text-xs sm:text-sm font-medium">
            <CheckCircle className="w-4 h-4 text-emerald-500 shrink-0" />
            <span>100% Client-side privacy (No files leave your computer)</span>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="space-y-3 pt-2">
          {isInstalled || installSuccess ? (
            <div className="w-full py-3 px-4 rounded-xl bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 font-bold text-center text-sm flex items-center justify-center gap-2">
              <CheckCircle className="w-5 h-5 text-emerald-500" />
              <span>Dualis Tools is already installed!</span>
            </div>
          ) : (
            <button
              id="confirm-install-pwa-btn"
              onClick={handleInstallClick}
              className="w-full py-3.5 px-6 rounded-xl font-bold text-sm bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white shadow-lg shadow-indigo-600/30 transition-all flex items-center justify-center gap-2.5 active:scale-[0.98]"
            >
              <Download className="w-4 h-4" />
              <span>Install to Device</span>
            </button>
          )}

          <button
            id="close-install-pwa-btn"
            onClick={onClose}
            className={`w-full py-2.5 text-xs font-semibold rounded-lg transition-colors ${
              isDark
                ? "text-slate-400 hover:text-white hover:bg-slate-800/50"
                : "text-slate-600 hover:text-slate-900 hover:bg-slate-100"
            }`}
          >
            Maybe Later
          </button>
        </div>
      </div>
    </div>
  );
};
